import { CsvButton } from "@/components/CsvButton";
import { downloadCsv, today } from "@/lib/csv";
import { AtgChip } from "@/components/AtgChip";
import { PlayerName } from "@/components/PlayerCardDialog";
import { ArchetypeBadge } from "@/components/ArchetypeBadge";
import { GoalOriginChart } from "@/components/GoalOriginChart";
import React from "react";
import { fmt } from "@/lib/format";
import { useTracker } from "@/lib/tracker";
import { Loader2, AlertTriangle } from "lucide-react";

const POS = ["C", "LW", "RW", "D"];
const val = (v, d = 2) => (v === null || v === undefined ? "—" : fmt(v, d));

const TAG_STYLE = {
  "GOAL-ORIGIN CORE": "border-rose-700/60 bg-rose-950/30 text-rose-300",
  "GOAL-ORIGIN VALUE": "border-cyan-700/60 bg-cyan-950/30 text-cyan-300",
  "CONTRARIAN": "border-amber-700/60 bg-amber-950/30 text-amber-300",
  "POSITIONAL ONLY": "border-slate-600/60 bg-slate-800/40 text-slate-300",
  "NO ORIGIN SUPPORT": "border-slate-700/50 bg-slate-900/40 text-slate-500",
};

const Section = ({ emoji, title, right, children, testId }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-3" data-testid={testId}>
    <div className="mb-2.5 flex items-center justify-between gap-2 border-b border-slate-800 pb-1.5">
      <h4 className="flex items-center gap-1.5 font-display text-[13px] font-bold uppercase tracking-wide text-slate-100">
        <span className="text-sm">{emoji}</span> {title}
      </h4>
      {right}
    </div>
    {children}
  </section>
);

const SourceBadge = ({ source }) => {
  const map = {
    CONFIRMED: "bg-lime-500 text-slate-950",
    PROJECTED: "bg-amber-950 text-amber-300 border border-amber-800",
    UNCONFIRMED: "bg-rose-950 text-rose-300 border border-rose-800",
    STALE: "bg-orange-950 text-orange-300 border border-orange-800",
  };
  const emoji = { CONFIRMED: "🟢", PROJECTED: "🟡", UNCONFIRMED: "🔴", STALE: "⚠️" };
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${map[source] || "bg-slate-800 text-slate-400"}`}>
      {source ? `${emoji[source] || ""} ${source}` : "—"}
    </span>
  );
};

// Goalie splits (position) + line-origin — must never be collapsed (section 19).
const GoaliePanel = ({ goalie }) => {
  if (!goalie) return null;
  const slots = Object.entries(goalie.line_freq || {}).sort((a, b) => b[1].goals - a[1].goals);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <div className="bg-slate-900/80 px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">
          Goalie Position Splits
        </div>
        <table className="w-full min-w-[320px] border-collapse">
          <thead>
            <tr className="bg-slate-900/50 font-mono text-[9px] uppercase tracking-wider text-slate-500">
              <th className="px-2 py-1 text-left">Pos</th>
              <th className="px-2 py-1 text-right">GA</th>
              <th className="px-2 py-1 text-right">Gm</th>
              <th className="px-2 py-1 text-right">1st</th>
              <th className="px-2 py-1 text-right">Multi</th>
              <th className="px-2 py-1 text-right">L5</th>
              <th className="px-2 py-1 text-right">L10</th>
            </tr>
          </thead>
          <tbody>
            {POS.map((p) => {
              const d = goalie.by_pos?.[p] || {};
              const top = goalie.total_ga > 0 && d.ga === Math.max(...POS.map((q) => goalie.by_pos?.[q]?.ga ?? 0));
              return (
                <tr key={p} className="border-t border-slate-800/60">
                  <td className={`px-2 py-1 font-mono text-[11px] font-bold ${top ? "text-rose-300" : "text-slate-300"}`}>{p}</td>
                  <td className={`px-2 py-1 text-right font-mono text-[11px] tabular-nums ${top ? "text-rose-300 font-bold" : "text-amber-300"}`}>{d.ga ?? "—"}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-400 tabular-nums">{d.games ?? "—"}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{d.first ?? "—"}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-300 tabular-nums">{d.multi ?? "—"}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 tabular-nums">{d.l5 ?? "—"}</td>
                  <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 tabular-nums">{d.l10 ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-800">
        <div className="bg-slate-900/80 px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">
          Line Origin (goals / games)
        </div>
        {slots.length ? (
          <div className="max-h-[168px] overflow-y-auto">
            <table className="w-full border-collapse">
              <tbody>
                {slots.map(([slot, d]) => (
                  <tr key={slot} className="border-t border-slate-800/60">
                    <td className="px-2 py-1 font-mono text-[11px] font-bold text-cyan-300">{slot}</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-200 tabular-nums">{d.goals}G</td>
                    <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 tabular-nums">{d.games}GM</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-3 text-center font-mono text-[11px] text-slate-600">Line data unavailable</p>
        )}
      </div>
    </div>
  );
};

const TargetTable = ({ targets, slotOrder, team }) => {
  const { isTracked, toggle } = useTracker();
  if (!targets?.length) return <p className="py-3 text-center font-mono text-[11px] text-slate-600">No current-lineup targets</p>;
  // Order players to match the Line Origin list order (slots ranked by goals);
  // any slot missing from line-origin keeps its original (score) order, last.
  const order = slotOrder || [];
  const rankOf = (s) => {
    const i = order.indexOf(s);
    return i === -1 ? 1e6 : i;
  };
  const rows = targets
    .map((t, i) => ({ t, i }))
    .sort((a, b) => rankOf(a.t.slot) - rankOf(b.t.slot) || a.i - b.i)
    .map((x) => x.t);
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full min-w-[760px] border-collapse">
        <thead>
          <tr className="bg-slate-900/80 font-mono text-[9px] uppercase tracking-wider text-slate-500">
            <th className="px-2 py-1.5 text-left">#</th>
            <th className="px-2 py-1.5 text-left">Player</th>
            <th className="px-2 py-1.5 text-left">Line</th>
            <th className="px-2 py-1.5 text-right" title="Historical goals / games from this exact line vs this goalie">Origin</th>
            <th className="px-2 py-1.5 text-right" title="Goalie goals allowed to this position">Pos GA</th>
            <th className="px-2 py-1.5 text-right" title="First-goal count for this position">1st</th>
            <th className="px-2 py-1.5 text-right" title="Multi-goal games for this position">Multi</th>
            <th className="px-2 py-1.5 text-right" title="Opponent GA/GP allowed to this position">Def</th>
            <th className="px-2 py-1.5 text-right">PP</th>
            <th className="px-2 py-1.5 text-right">Score</th>
            <th className="px-2 py-1.5 text-left">Tag</th>
            <th className="px-2 py-1.5 text-right">Track</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, idx) => (
            <tr key={`${t.jersey}-${t.slot}-${idx}`} className={`border-t border-slate-800/60 ${idx === 0 ? "bg-rose-950/15" : ""}`} data-testid={`cypher-row-${t.jersey}`}>
              <td className="px-2 py-1.5 font-mono text-[12px] font-bold text-cyan-400">{idx + 1}</td>
              <td className="px-2 py-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-mono text-[12px] font-bold text-cyan-400">{t.jersey != null ? `#${t.jersey}` : "#—"}</span>
                  <PlayerName name={t.player} team={team} className="font-display text-[12px] font-semibold uppercase tracking-tight text-slate-100" />
                  <ArchetypeBadge name={t.archetype} testId={`target-arch-${t.jersey}`} />
                  <AtgChip atg={t.atg} testId={`target-atg-${t.jersey}`} />
                  <span className="rounded bg-slate-800 px-1 font-mono text-[9px] font-semibold text-slate-300">{t.position}</span>
                </span>
                {t.exact_line_match && (
                  <span className="ml-1 rounded bg-cyan-950 px-1 font-mono text-[8px] font-bold uppercase text-cyan-300">exact line</span>
                )}
                {t.note && <div className="mt-0.5 font-mono text-[9px] text-slate-500">{t.note}</div>}
              </td>
              <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">{t.slot}</td>
              <td className={`px-2 py-1.5 text-right font-mono text-[11px] tabular-nums ${t.origin_goals > 0 ? "font-bold text-lime-300" : "text-slate-600"}`}>
                {t.origin_goals}G / {t.origin_games}GM
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-[11px] text-amber-300 tabular-nums">{t.position_goals}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-300 tabular-nums">{t.first_goal}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-300 tabular-nums">{t.multi_goal}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-400 tabular-nums">{val(t.def_ga_gp)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[10px] text-amber-400">{t.pp_unit || "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono text-[12px] font-bold text-cyan-200 tabular-nums">{t.score}</td>
              <td className="px-2 py-1.5">
                <span className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${TAG_STYLE[t.tag_label] || "border-slate-700 text-slate-400"}`}>
                  {t.tag} {t.tag_label}
                </span>
              </td>
              <td className="px-2 py-1.5 text-right">
                <button
                  data-testid={`track-btn-${t.jersey}`}
                  onClick={() => toggle({ ...t, name: t.player, line: t.slot })}
                  className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider transition ${
                    isTracked({ ...t, name: t.player }) ? "bg-lime-500 text-slate-950" : "border border-cyan-800 text-cyan-300 hover:bg-cyan-950"
                  }`}
                >
                  {isTracked({ ...t, name: t.player }) ? "✓" : "+"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SideBlock = ({ side }) => {
  if (!side) return null;
  if (side.status !== "OK") {
    return (
      <div className="rounded-xl border border-amber-900/50 bg-amber-950/10 p-4 text-center" data-testid={`cypher-watch-${side.attacker}`}>
        <AlertTriangle className="mx-auto mb-2 h-5 w-5 text-amber-500" />
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Status: Watch</p>
        <p className="mt-1 font-mono text-[11px] text-slate-400">{side.watch_reason}</p>
      </div>
    );
  }
  const g = side.goalie;
  const slotOrder = Object.entries(g.line_freq || {})
    .sort((a, b) => b[1].goals - a[1].goals)
    .map(([slot]) => slot);
  return (
    <Section
      emoji="🥅"
      title={`${side.attacker} vs ${g.name}`}
      right={
        <span className="flex items-center gap-1.5">
          <SourceBadge source={g.source} />
          <span className="font-mono text-[9px] uppercase text-slate-500">{g.gp} GP · {g.total_ga} GA</span>
        </span>
      }
      testId={`cypher-side-${side.attacker}`}
    >
      <div className="space-y-2.5">
        <GoaliePanel goalie={g} />
        <TargetTable targets={side.targets} slotOrder={slotOrder} team={side.attacker} />
      </div>
    </Section>
  );
};

const Legend = () => (
  <div className="flex flex-wrap gap-2 rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2">
    {[
      ["🔥", "CORE", "text-rose-300"],
      ["🎯", "VALUE", "text-cyan-300"],
      ["🧨", "CONTRARIAN", "text-amber-300"],
      ["⚠️", "POSITIONAL ONLY", "text-slate-300"],
      ["❄️", "NO ORIGIN", "text-slate-500"],
    ].map(([e, l, c]) => (
      <span key={l} className={`font-mono text-[9px] font-bold uppercase tracking-wider ${c}`}>
        {e} {l}
      </span>
    ))}
  </div>
);

export const TargetsTab = ({ analysis, loading, error, awayAbbr, homeAbbr, onRetry }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest">Running goal-origin engine…</p>
      </div>
    );
  }
  if (error || !analysis?.cypher) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <p className="font-mono text-[11px] uppercase tracking-widest text-rose-400">Goal-origin data unavailable</p>
        <button onClick={onRetry} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
      </div>
    );
  }
  const cy = analysis.cypher;
  const exportTargets = () => downloadCsv(`goal-origin-targets_${awayAbbr}-at-${homeAbbr}_${today()}.csv`,
    ["away", "home"].flatMap((k) => (cy[k]?.targets || []).map((t, i) => ({
      team: cy[k].attacker, opponent: cy[k].defender, opp_goalie: cy[k].goalie?.name, goalie_status: cy[k].goalie?.status,
      rank: i + 1, player: t.player, jersey: t.jersey, position: t.position, slot: t.slot, pp_unit: t.pp_unit,
      score: t.score, tag: t.tag_label, origin_goals: t.origin_goals, origin_games: t.origin_games,
      first_goal: t.first_goal, multi_goal: t.multi_goal, exact_line_match: t.exact_line_match,
      team_weakness: t.scores?.team_weakness, goalie_origin: t.scores?.goalie_origin, exact_line: t.scores?.exact_line,
      activation: t.scores?.activation, form_env: t.scores?.form_env, season_form: t.scores?.season_form,
      archetype: t.archetype?.primary, atg_best: t.atg?.best, atg_book: t.atg?.best_book, atg_implied_pct: t.atg?.implied,
      model_pct: t.atg?.model, edge: t.atg?.edge, value: t.atg?.value ? "YES" : "", injury: t.injury,
    }))));
  return (
    <div className="space-y-3" data-testid={`cypher-analysis-${awayAbbr}-${homeAbbr}`}>
      <div className="rounded-xl border border-cyan-900/40 bg-gradient-to-r from-cyan-950/20 to-slate-900/20 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-[13px] font-bold uppercase tracking-wide text-cyan-300">CYPHER Goal-Origin Engine</p>
          <CsvButton onClick={exportTargets} label="Targets CSV" testId={`csv-targets-${awayAbbr}-${homeAbbr}`} />
        </div>
        <p className="mt-0.5 font-mono text-[10px] leading-relaxed text-slate-400">
          Who has actually scored from this position & line against this goalie — and who currently occupies that pathway.
        </p>
      </div>
      <Legend />
      <SideBlock side={cy.away} />
      <SideBlock side={cy.home} />
      <GoalOriginChart awayAbbr={awayAbbr} homeAbbr={homeAbbr} cypher={cy} />
    </div>
  );
};

export default TargetsTab;
